/** Visitor analytics: GA4 and/or Plausible, whichever has an id in the build
 * (VITE_GA4_ID, VITE_PLAUSIBLE_DOMAIN; frontend Dockerfile build args).
 *
 * Plausible keeps no cookies, so it runs straight away. GA4 sets cookies, so
 * it loads only after the visitor presses «Прийняти» in the consent banner;
 * «Відхилити» means it never loads. Without ids nothing loads at all and
 * track() does nothing. The choice lives in localStorage and can be changed
 * from «Налаштування cookie» in the site footer.
 *
 * Public pages count page views. The cabinet sends only its conversion
 * events (sign-up, setup finished, test call), never its own URLs. */

const GA4_ID = import.meta.env.VITE_GA4_ID
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN
const PLAUSIBLE_SRC = import.meta.env.VITE_PLAUSIBLE_SRC || 'https://plausible.io/js/script.js'
const CONSENT_KEY = 'anytel:analytics-consent'

/** GA4 is configured, so the site shows the consent banner and the footer
 * link. Known at build time: the same on the server and in the browser. */
export const needsConsent = Boolean(GA4_ID)

type Props = Record<string, string | number | boolean>
type Consent = 'granted' | 'denied'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    plausible?: ((event: string, options?: { props?: Props }) => void) & { q?: unknown[] }
  }
}

let started = false
let pageviews = true
let gaOn = false

/** One event, to every analytics that is on. Safe to call anywhere, any time. */
export function track(event: string, props: Props = {}) {
  if (typeof window === 'undefined') return
  if (gaOn) window.gtag?.('event', event, props)
  if (!PLAUSIBLE_DOMAIN) return
  if (pageviews) {
    window.plausible?.(event, { props })
  } else {
    // The cabinet doesn't load the Plausible script (it would count cabinet
    // URLs as page views), so its few events go to the events API directly.
    fetch(new URL('/api/event', PLAUSIBLE_SRC).href, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ name: event, url: `${location.origin}/app`, domain: PLAUSIBLE_DOMAIN, props }),
    }).catch(() => {})
  }
}

function readConsent(): Consent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

function writeConsent(v: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, v)
  } catch {
    // Storage off: the choice holds for this page only, the banner asks again.
  }
}

function addScript(src: string, attrs: Record<string, string> = {}) {
  const s = document.createElement('script')
  s.async = true
  s.src = src
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v))
  document.head.appendChild(s)
}

/** Scripts from other origins wait for the visitor's first interaction or
 * a few seconds after load, whichever comes first: requested during the
 * first paint, a third-party connection pushed the mobile LCP from 2.6 to
 * 3.3 s in Lighthouse. Events are queued meanwhile (dataLayer, the
 * plausible() stub), so nothing is lost. */
let interacted: Promise<void> | null = null
function whenInteracted(): Promise<void> {
  interacted ??= new Promise<void>((resolve) => {
    const events = ['scroll', 'pointerdown', 'keydown', 'touchstart'] as const
    const go = () => {
      events.forEach((e) => window.removeEventListener(e, go, true))
      resolve()
    }
    events.forEach((e) => window.addEventListener(e, go, { capture: true, passive: true }))
  })
  return interacted
}

let quiet: Promise<void> | null = null
function whenQuiet(): Promise<void> {
  quiet ??= Promise.race([
    whenInteracted(),
    new Promise<void>((resolve) => {
      const later = () => window.setTimeout(resolve, 3000)
      if (document.readyState === 'complete') later()
      else window.addEventListener('load', later, { once: true })
    }),
  ])
  return quiet
}

function loadGa() {
  if (gaOn || !GA4_ID) return
  gaOn = true
  window.dataLayer = window.dataLayer || []
  // gtag.js reads the `arguments` object itself, not an array.
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, { send_page_view: pageviews })
  const id = GA4_ID
  void whenQuiet().then(() => addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`))
}

function stopGa() {
  gaOn = false
  // Drop what GA4 already stored: _ga and _ga_<id> on this domain and its parent.
  const host = location.hostname
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-3).join('.')}`]
  for (const c of document.cookie.split(';')) {
    const name = c.split('=')[0].trim()
    if (!name.startsWith('_ga')) continue
    for (const d of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`
    }
  }
}

function loadPlausible() {
  if (!PLAUSIBLE_DOMAIN || !pageviews) return
  window.plausible = window.plausible || Object.assign(
    function plausible(...args: unknown[]) {
      (window.plausible!.q = window.plausible!.q || []).push(args)
    },
    { q: [] as unknown[] },
  )
  const domain = PLAUSIBLE_DOMAIN
  void whenQuiet().then(() => addScript(PLAUSIBLE_SRC, { 'data-domain': domain }))
}

/* ── Consent banner ──────────────────────────────────────────────────── */

// Compact on purpose: it must not hide the demo number on a phone, and it
// must stay smaller than the headline, or it becomes the page's LCP.
const BANNER_CSS = `
.vp-consent{position:fixed;z-index:2147483000;left:12px;right:12px;bottom:12px;margin:0 auto;max-width:560px;
display:flex;flex-wrap:wrap;align-items:center;gap:10px 14px;padding:12px 14px;border-radius:14px;
font:13.5px/1.45 'Onest Variable',system-ui,sans-serif;background:#17181c;color:#eeeef0;
border:1px solid rgb(255 255 255/.14);box-shadow:0 16px 48px -18px rgb(0 0 0/.6)}
.vp-consent p{margin:0;flex:1 1 260px}
.vp-consent a{color:inherit;text-decoration:underline;text-underline-offset:3px}
.vp-consent-actions{display:flex;gap:8px;flex:1 1 200px}
.vp-consent button{flex:1;height:38px;padding:0 14px;border-radius:999px;font:inherit;font-weight:600;cursor:pointer;
border:1px solid rgb(255 255 255/.24);background:transparent;color:inherit}
.vp-consent button[data-choice=granted]{background:#c6f25e;border-color:#c6f25e;color:#12160a}
.vp-consent button:focus-visible{outline:2px solid #c6f25e;outline-offset:3px}
@media (prefers-color-scheme:light){.vp-consent{background:#fff;color:#121317;border-color:rgb(16 18 22/.16);
box-shadow:0 16px 40px -20px rgb(30 40 60/.45)}.vp-consent button{border-color:rgb(16 18 22/.2)}
.vp-consent button[data-choice=granted]{border-color:#9cc23a}}
`

function showBanner() {
  if (document.querySelector('.vp-consent')) return
  if (!document.getElementById('vp-consent-css')) {
    const style = document.createElement('style')
    style.id = 'vp-consent-css'
    style.textContent = BANNER_CSS
    document.head.appendChild(style)
  }
  const box = document.createElement('section')
  box.className = 'vp-consent'
  box.setAttribute('aria-label', 'Згода на аналітичні cookie')
  box.innerHTML =
    '<p>Дозволите cookie Google Analytics для статистики відвідувань? Без них сайт працює так само. '
    + '<a href="/privacy">Детальніше</a></p>'
    + '<div class="vp-consent-actions">'
    + '<button type="button" data-choice="denied">Відхилити</button>'
    + '<button type="button" data-choice="granted">Прийняти</button>'
    + '</div>'
  box.addEventListener('click', (e) => {
    const choice = (e.target as HTMLElement).closest('button')?.dataset.choice as Consent | undefined
    if (!choice) return
    writeConsent(choice)
    box.remove()
    if (choice === 'granted') {
      loadGa()
      track('consent_granted')
    } else {
      stopGa()
    }
  })
  document.body.appendChild(box)
}

/** Footer link «Налаштування cookie»: ask again. */
export function openConsentSettings() {
  showBanner()
}

/* ── Start ───────────────────────────────────────────────────────────── */

/** Clicks worth counting on any page, wherever the link sits. */
function listen() {
  document.addEventListener(
    'click',
    (e) => {
      const a = (e.target as Element | null)?.closest?.('a[href]')
      if (!a) return
      const href = a.getAttribute('href') ?? ''
      const placement = a.closest('[id]:not(#root)')?.id || a.closest('header, footer')?.tagName.toLowerCase() || 'page'
      if (href.startsWith('tel:')) track('demo_call_click', { placement })
      else if (href === '/register') track('signup_cta_click', { placement })
      else if (href.endsWith('#pilot')) track('pilot_cta_click', { placement })
    },
    { capture: true },
  )
  // `play` doesn't bubble; capturing catches every <audio> on the page.
  document.addEventListener(
    'play',
    (e) => {
      if (e.target instanceof HTMLAudioElement) track('demo_audio_play', { audio: e.target.getAttribute('src') ?? '' })
    },
    true,
  )
}

export function initAnalytics(options: { pageviews: boolean }) {
  if (started || (!GA4_ID && !PLAUSIBLE_DOMAIN)) return
  started = true
  pageviews = options.pageviews
  loadPlausible()
  if (GA4_ID) {
    const consent = readConsent()
    if (consent === 'granted') loadGa()
    // Only once the visitor scrolls, taps or types: shown on load it covered
    // the demo number on phones, and painted late it became the page's LCP.
    else if (consent === null) void whenInteracted().then(showBanner)
  }
  if (pageviews) listen()
}
